import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ShieldOff, Plus } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

export default function DNC() {
  const [items, setItems] = useState([]);
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("manual");

  const load = async () => {
    try {
      const { data } = await api.get("/dnc");
      setItems(data.items || []);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!phone) return;
    try {
      await api.post("/dnc", { phone, reason });
      toast.success("Added to DNC");
      setPhone("");
      setReason("manual");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add");
    }
  };

  return (
    <div data-testid="dnc-page">
      <div className="mb-8">
        <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 mb-2">
          Compliance
        </div>
        <h1 className="text-3xl font-medium tracking-tight text-white">DNC List</h1>
        <p className="text-sm text-white/50 mt-2">
          Internal Do Not Call registry. Numbers here are skipped in every campaign.
        </p>
      </div>

      <form
        onSubmit={add}
        data-testid="add-dnc-form"
        className="bg-[#121214] border border-white/10 rounded-xl p-5 mb-6 flex flex-wrap gap-3 items-end"
      >
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-medium text-white/50 uppercase tracking-[0.1em] mb-2 block">
            Phone Number
          </label>
          <input
            data-testid="dnc-phone-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+14155552671"
            className="w-full bg-[#0a0a0b] border border-white/10 rounded-md text-white text-sm px-3.5 py-2.5 font-mono placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-[#FFB000] focus:border-[#FFB000]"
          />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="text-xs font-medium text-white/50 uppercase tracking-[0.1em] mb-2 block">
            Reason
          </label>
          <input
            data-testid="dnc-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full bg-[#0a0a0b] border border-white/10 rounded-md text-white text-sm px-3.5 py-2.5 placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-[#FFB000] focus:border-[#FFB000]"
          />
        </div>
        <button
          data-testid="add-dnc-btn"
          type="submit"
          className="bg-[#FFB000] hover:bg-[#FFC133] text-black font-medium text-sm px-4 py-2.5 rounded-md transition-colors flex items-center gap-2 shadow-[0_0_18px_rgba(255,176,0,0.15)]"
        >
          <Plus className="w-4 h-4" />
          Add
        </button>
      </form>

      <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
          <ShieldOff className="w-4 h-4 text-red-500" />
          <div className="text-sm font-medium text-white">{items.length} numbers on DNC</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="dnc-table">
            <thead>
              <tr className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                <th className="text-left px-5 py-3 font-normal">Phone</th>
                <th className="text-left px-5 py-3 font-normal">Reason</th>
                <th className="text-left px-5 py-3 font-normal">Added</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-5 py-10 text-center text-white/40 text-sm">
                    DNC list empty.
                  </td>
                </tr>
              )}
              {items.map((d) => (
                <tr key={d.phone} className="hover:bg-white/[0.02]">
                  <td className="px-5 py-3 font-mono text-red-400">{d.phone}</td>
                  <td className="px-5 py-3 text-white/60">{d.reason || "—"}</td>
                  <td className="px-5 py-3 font-mono text-white/40 text-xs">
                    {d.added_at ? format(new Date(d.added_at), "MMM d, yyyy · HH:mm") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
